import { useCallback } from 'react';
import { openBrowserAsync } from 'expo-web-browser';

import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

/**
 * Featured flagged contact us handler.
 * Will open Sierra if its enabled.
 * Fallback to opening https://www.sweetgreen.com/contact.
 */
export const useContactUsHandler = (
  source: 'account' | 'order-history' | 'org-links' | 'order-status' | 'scan' | 'home',
) => {
  const { track } = useTelemetry();

  const isSierraEnabled = useFeatureFlag('permanent-sierra-enabled');
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const navigation = useSafeNavigation();
  const router = useSafeRouter();

  const handleContactUs = useCallback(async () => {
    track('contact_us', {
      source,
      destination: getDestination(isSierraEnabled),
    });

    if (isSierraEnabled) {
      if (isExpoRouterEnabled) {
        router?.navigate('/chat');
        return;
      }

      navigation?.navigate('Modal', { screen: 'Chat' });
      return;
    }

    await openBrowserAsync('https://www.sweetgreen.com/contact', {
      windowFeatures: { target: '_blank' },
    });
  }, [source, isSierraEnabled, isExpoRouterEnabled, router, navigation, track]);

  return {
    handleContactUs,
    hasDynamicChat: isSierraEnabled,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDestination = (isSierraEnabled: boolean) => {
  if (isSierraEnabled) return 'sierra';

  return 'website';
};
